package com.klantenstop.web.utils

import com.klantenstop.web.bindings.PopupContent
import com.klantenstop.web.data.models.domain.MapPinnable
import com.klantenstop.web.data.models.domain.Status
import com.klantenstop.web.navigation.Navigation
import com.materialkobweb.styles.MaterialColorVars
import com.klantenstop.web.styles.staticAcceptColor
import com.klantenstop.web.styles.staticDeclineColor
import com.klantenstop.web.styles.staticPauseColor
import com.klantenstop.web.styles.staticUnknownColor
import com.materialkobweb.extensions.toHex
import com.varabyte.kobweb.navigation.BasePath
import com.varabyte.kobweb.navigation.Router
import kotlinx.browser.document
import org.w3c.dom.HTMLAnchorElement
import org.w3c.dom.HTMLDivElement
import org.w3c.dom.HTMLHeadingElement
import org.w3c.dom.HTMLParagraphElement
import org.w3c.dom.HTMLSpanElement

/**
 * Extension function to generate DOM element for map marker popups.
 * Uses DOM API to build structured HTML with proper event handlers for SPA navigation.
 * Returns PopupContent.Element so event handlers survive Leaflet's popup binding.
 */
fun MapPinnable.toMapPopupHtml(router: Router): PopupContent.Element {
    val container = document.createElement("div") as HTMLDivElement
    container.apply {
        style.apply {
            fontFamily = "system-ui, -apple-system, sans-serif"
            minWidth = "200px"
            maxWidth = "280px"
        }

        // Company name
        val nameElement = document.createElement("h3") as HTMLHeadingElement
        nameElement.apply {
            textContent = this@toMapPopupHtml.name
            style.apply {
                margin = "0 0 8px 0"
                fontSize = "16px"
                fontWeight = "600"
                color = MaterialColorVars.OnSurface.value().toString()
                lineHeight = "1.3"
            }
        }
        appendChild(nameElement)

        // Address
        val address = this@toMapPopupHtml.address
        if (address != null) {
            val addressElement = document.createElement("p") as HTMLParagraphElement
            addressElement.apply {
                textContent = address.fullAddress
                style.apply {
                    margin = "0 0 10px 0"
                    fontSize = "13px"
                    color = MaterialColorVars.OnSurface.value().toString()
                    lineHeight = "1.4"
                }
            }
            appendChild(addressElement)
        }

        // Status badge
        val statusBadge = document.createElement("span") as HTMLSpanElement
        statusBadge.apply {
            textContent = this@toMapPopupHtml.statusText.takeIf { it.isNotBlank() }
                ?: this@toMapPopupHtml.statusType.toText()

            style.apply {
                display = "inline-block"
                padding = "4px 10px"
                borderRadius = "12px"
                fontSize = "12px"
                fontWeight = "500"
                marginBottom = "10px"

                val statusColor = when (this@toMapPopupHtml.statusType) {
                    Status.Type.Accept -> staticAcceptColor
                    Status.Type.Pause -> staticPauseColor
                    Status.Type.Decline -> staticDeclineColor
                    Status.Type.Unknown -> staticUnknownColor
                }
                backgroundColor = statusColor.toHex()
                color = "#FFFFFF"
            }
        }
        appendChild(statusBadge)

        // View details link
        val link = document.createElement("a") as HTMLAnchorElement
        link.apply {
            textContent = "Bekijk details"
            href = BasePath.prependTo(Navigation.Screen.CompanyDetail(this@toMapPopupHtml.publicIdentifier).route)
            style.apply {
                width = "fit-content"
                display = "block"
                marginTop = "10px"
                color = MaterialColorVars.Primary.value().toString()
                textDecoration = "none"
                fontSize = "14px"
                fontWeight = "500"
                cursor = "pointer"
            }
            // SPA navigation instead of full page refresh
            onclick = { event ->
                router.navigateTo(Navigation.Screen.CompanyDetail(this@toMapPopupHtml.publicIdentifier).route)
                event.preventDefault()
                event.stopPropagation()
            }
            // Add hover effect via inline event handler
            onmouseover = { style.textDecoration = "underline"; null }
            onmouseout = { style.textDecoration = "none"; null }
        }
        appendChild(link)
    }

    return PopupContent.Element(container)
}
